import React from 'react';
import {Box, Typography} from '@mui/material';
import {NAVY, ORANGE, TEAL, MarkerBar} from './widgets.comp';
import {PASS_THRESHOLD, PASS_THRESHOLD_LABEL} from './programImpact';
import {SuppressedValue} from './SuppressedValue.comp';

/* "Impact by Class · Pass Rate" — the Program Impact card shared by the Teacher
   (ProgramImpactDashboard) and School pages. `byClass` is classRowsFrom(...).
   Each row puts its bar on a line of its own: in the two-column (lg) layout the
   card is too narrow for label + impact + bar + % + chip on one line, which
   squeezed the bar to 0px. */

const StatusChip = ({status}) => (
    <Box sx={{
        display: 'inline-block', backgroundColor: status === 'Pass' ? TEAL : (status === 'Not Pass' ? '#E9721C' : '#9CA3AF'),
        color: '#fff', fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.4,
        borderRadius: '20px', whiteSpace: 'nowrap', minWidth: '78px', textAlign: 'center',
    }}>
        {status}
    </Box>
);

export const ClassPassRateCard = ({byClass}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        p: 3, display: 'flex', flexDirection: 'column', minWidth: 0,
    }}>
        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2}}>
            Impact by Class · Pass Rate (Post ≥ {PASS_THRESHOLD})
        </Typography>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            {!byClass.length && (
                <Typography sx={{fontSize: '13px', color: '#6A7282'}}>No classes assigned yet.</Typography>
            )}
            {byClass.map((c, ci) => (
                <Box key={`${c.label}-${ci}`}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                        <Typography noWrap title={c.label} sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, flex: 1, minWidth: 0}}>
                            {c.label}
                        </Typography>
                        {c.suppressed ? <Box sx={{minWidth: 0}}><SuppressedValue/></Box> : (<>
                        <Typography sx={{fontSize: '13px', fontWeight: 700, color: ORANGE, width: '44px', flexShrink: 0}}>
                            {c.impact}
                        </Typography>
                        <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, width: '38px', flexShrink: 0, textAlign: 'right'}}>
                            {c.passRate !== null ? `${Math.round(c.passRate)}%` : '—'}
                        </Typography>
                        <Box sx={{flexShrink: 0}}>
                            {c.status && c.status !== '—' && <StatusChip status={c.status}/>}
                        </Box>
                        </>)}
                    </Box>
                    {/* The ▼ threshold marker sits 9px above the track, hence the top margin. */}
                    {!c.suppressed && (
                        <Box sx={{mt: 1.5}}>
                            <MarkerBar score={c.passRate} color={c.status === 'Pass' ? TEAL : '#E9721C'} national={PASS_THRESHOLD} nationalLabel={PASS_THRESHOLD_LABEL} unit="percent" bordered/>
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
        <Typography sx={{fontSize: '12.5px', color: '#6A7282', lineHeight: 1.7, mt: 2.5}}>
            A class passes when its pass rate is {PASS_THRESHOLD}% or above (▼ marker). Below that,
            chase outstanding completions before treating the class result as representative.
        </Typography>
        <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 1}}>
            {`▼ ${PASS_THRESHOLD}% pass threshold`}
        </Typography>
    </Box>
);

export default ClassPassRateCard;
